import type { CitationChipData } from '@/components/ui/citation-chip';
import type {
  CandidateRefusal,
  IntakeCandidate,
  IntakeCitation,
  IntakeInDocumentMatch,
  IntakeLocation,
  IntakeMatch,
  IntakeRun,
} from '@/lib/api/requirement-library-intake';
import { progressLabel, progressPercent } from '@/lib/utils/processing';
import { matchKindWords, plural } from '../library-logic';
import { formatDateTime } from '@/lib/utils/format';

/**
 * Pure logic of the AI document intake review (T-0154; E11-F08). No React
 * and no requests: which files may be sent, the words for a run's status
 * and for the server's refusals, the review filters, and the counts shown
 * in the footer.
 *
 * Nothing here scores, ranks or selects candidates. Confidence, duplicate
 * matches, `low_confidence` and `bulk_acceptable` are the server's; this
 * file only counts and words what it returned.
 */

// ---------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------

export const MAX_INTAKE_FILE_BYTES = 50 * 1024 * 1024;
export const INTAKE_FILE_EXTENSIONS = ['.pdf', '.docx', '.xlsx', '.xls', '.csv', '.txt'] as const;
export const INTAKE_ACCEPT = INTAKE_FILE_EXTENSIONS.join(',');

/**
 * Why a file cannot be sent, or null when it can. A first check only: the
 * server decides what it can read.
 */
export function intakeFileProblem(file: { name: string; size: number }): string | null {
  const name = file.name.toLowerCase();
  if (!INTAKE_FILE_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return `${file.name} is not a PDF, Word (.docx), Excel, CSV or text file.`;
  }
  if (file.size === 0) return `${file.name} is empty.`;
  if (file.size > MAX_INTAKE_FILE_BYTES) {
    return `${file.name} is larger than 50 MB. Split it and import the parts.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Run status
// ---------------------------------------------------------------------------

export type RunTone = 'neutral' | 'ai' | 'at-risk' | 'critical' | 'healthy';

/** The status of a run in plain words, for the runs list and the sheet. */
export function runStatusLabel(run: Pick<IntakeRun, 'status' | 'outcome'>): string {
  switch (run.status) {
    case 'created':
      return 'Waiting to start';
    case 'parsing':
      return 'Reading the pages';
    case 'extracting':
      return 'Finding requirements';
    case 'ready_for_review':
      return run.outcome === 'none_found' ? 'Nothing found' : 'Ready for review';
    case 'confirmed':
      return 'Added to drafts';
    case 'cancelled':
      return 'Cancelled';
    case 'failed':
      return 'Failed';
    default:
      return run.status;
  }
}

export function runStatusTone(run: Pick<IntakeRun, 'status' | 'outcome'>): RunTone {
  switch (run.status) {
    case 'created':
    case 'parsing':
    case 'extracting':
      return 'ai';
    case 'ready_for_review':
      return run.outcome === 'none_found' ? 'neutral' : 'at-risk';
    case 'confirmed':
      return 'healthy';
    case 'failed':
      return 'critical';
    default:
      return 'neutral';
  }
}

/**
 * A document's length in its own terms (T-0155): "38 pages", "3 sheets", or
 * nothing for a file that records no pages (the parser stores it as one).
 */
export function pageCountWords(
  count: number | null | undefined,
  pageSource: string | null | undefined,
): string | null {
  if (!count || pageSource === 'none') return null;
  return plural(count, pageSource === 'sheets' ? 'sheet' : 'page');
}

/** What the reader is doing right now, under "Reading <file>…". */
export function runProgressLine(run: IntakeRun): string {
  const length = pageCountWords(
    run.counts.pages ?? run.document.page_count,
    run.document.page_source,
  );
  const pageText = length ? `${length}. ` : '';
  switch (run.status) {
    case 'created':
      return 'Waiting for the reader to pick it up.';
    case 'parsing':
      return 'Turning the document into pages.';
    case 'extracting':
      return `${pageText}Finding requirement statements, then checking each against the library.`;
    default:
      return '';
  }
}

/**
 * Real progress of the reading stage (T-0159): sections (windows) of the
 * document read by the model over the sections built, from the run's own
 * counts. Null when the run is not reading or the count is not known yet —
 * no number is ever estimated.
 */
export function runReadingProgress(
  run: Pick<IntakeRun, 'status' | 'counts'>,
): { percent: number; label: string } | null {
  if (run.status !== 'extracting') return null;
  const total = run.counts.windows ?? null;
  const done = run.counts.windows_done ?? null;
  if (total === null || done === null || total <= 0) return null;
  if (done >= total) {
    return {
      percent: 100,
      label: `Read all ${plural(total, 'section')} · checking each against the library…`,
    };
  }
  const percent = progressPercent({ done, total }) ?? 0;
  return {
    percent,
    label: progressLabel('Finding requirements', { done, total }, 'section'),
  };
}

/**
 * A run still waiting to be picked up this long after it started gets a
 * note. The server says so itself (`handoff.waiting_for_reader`); this is
 * the fallback for a response without it.
 */
export const WAITING_NOTICE_AFTER_MS = 20_000;

/**
 * T-0156: a note for a run that has not moved for a while. Nothing is asked
 * of the curator: the hand-off to the reader is retried automatically, and a
 * reader that stopped is restarted. Null when the run is moving normally.
 */
export function runWaitingNotice(
  run: Pick<IntakeRun, 'status' | 'created_at' | 'handoff'>,
  now: number,
): string | null {
  if (run.status === 'created') {
    const started = Date.parse(run.created_at);
    const waiting =
      run.handoff?.waiting_for_reader ??
      (Number.isFinite(started) && now - started >= WAITING_NOTICE_AFTER_MS);
    if (!waiting) return null;
    return 'Waiting for the document reader to pick this up. If it does not start soon it is handed over again automatically, so there is nothing you need to do.';
  }
  if ((run.status === 'parsing' || run.status === 'extracting') && run.handoff?.stalled) {
    return 'The document reader has not reported progress for a while. It is restarted automatically, so there is nothing you need to do.';
  }
  return null;
}

/** Failures the recovery sweep records (T-0156): the fix is to start again. */
export function isRecoveryFailure(run: Pick<IntakeRun, 'error'>): boolean {
  const code = run.error?.code;
  return code === 'handoff_failed' || code === 'worker_stalled';
}

/** Why a run failed, in a sentence. The server's own message is used when it has one. */
export function runFailureMessage(run: IntakeRun): string {
  const code = run.error?.code ?? '';
  if (code === 'handoff_failed') {
    return 'The document reader never picked this document up, even after several automatic retries. Nothing was read and nothing was charged. Start again to try once more.';
  }
  if (code === 'worker_stalled') {
    return 'The document reader stopped part-way through this document and did not recover after several automatic retries. Nothing was kept. Start again to read it from the beginning.';
  }
  if (code === 'ai.budget_exhausted') {
    return 'The firm AI budget for this month ran out while the document was being read. Nothing was added.';
  }
  if (code === 'no_text') {
    return 'No readable text was found in this document. A scanned document without a text layer cannot be read.';
  }
  if (code === 'parse_failed') {
    return (
      run.error?.message || 'The document could not be opened. It may be damaged or protected.'
    );
  }
  if (code === 'document_withdrawn') {
    return 'This document was withdrawn by a firm admin while it was being read.';
  }
  return run.error?.message || 'The document could not be read. Nothing was added.';
}

/** One line of counts for a run in the list: candidates, then what became of them. */
export function runCountsLine(run: IntakeRun): string {
  const c = run.counts;
  if (run.status === 'failed' || run.status === 'cancelled') return 'Nothing added';
  if (run.status !== 'ready_for_review' && run.status !== 'confirmed') return 'In progress';
  const found = c.candidates ?? 0;
  if (found === 0) return 'No candidates';
  const parts = [plural(found, 'candidate')];
  if (run.status === 'confirmed') parts.push(`${(c.accepted ?? 0).toLocaleString()} added`);
  else {
    parts.push(`${(c.accepted ?? 0).toLocaleString()} accepted`);
    parts.push(`${(c.undecided ?? 0).toLocaleString()} undecided`);
  }
  return parts.join(' · ');
}

// ---------------------------------------------------------------------------
// Candidates
// ---------------------------------------------------------------------------

export type CandidateFilter = 'all' | 'new' | 'duplicates' | 'low';

export function matchesCandidateFilter(c: IntakeCandidate, filter: CandidateFilter): boolean {
  switch (filter) {
    case 'new':
      return !c.has_duplicate;
    case 'duplicates':
      return c.has_duplicate;
    case 'low':
      return c.low_confidence;
    default:
      return true;
  }
}

export interface CandidateCounts {
  total: number;
  /** Not flagged as resembling anything. */
  fresh: number;
  duplicates: number;
  lowConfidence: number;
  accepted: number;
  skipped: number;
  undecided: number;
  /** What the server's bulk accept would take right now. */
  bulkAcceptable: number;
}

/** Tallies of the candidates as the server returned them. */
export function candidateCounts(candidates: ReadonlyArray<IntakeCandidate>): CandidateCounts {
  const counts: CandidateCounts = {
    total: candidates.length,
    fresh: 0,
    duplicates: 0,
    lowConfidence: 0,
    accepted: 0,
    skipped: 0,
    undecided: 0,
    bulkAcceptable: 0,
  };
  for (const c of candidates) {
    if (c.has_duplicate) counts.duplicates += 1;
    else counts.fresh += 1;
    if (c.low_confidence) counts.lowConfidence += 1;
    if (c.decision === 'accept') counts.accepted += 1;
    else if (c.decision === 'skip') counts.skipped += 1;
    else counts.undecided += 1;
    if (c.bulk_acceptable) counts.bulkAcceptable += 1;
  }
  return counts;
}

/** "Same as AP-002 in Accounts Payable" / "Similar to …": the heading of a match box. */
export function matchHeading(match: Pick<IntakeMatch, 'match_kind'>): string {
  return match.match_kind === 'exact' ? 'Same as' : 'Similar to';
}

/** The kind of match in plain words; "similar" when the server sent a kind this screen does not know. */
export function matchKindLabel(match: Pick<IntakeMatch | IntakeInDocumentMatch, 'match_kind'>) {
  return matchKindWords(match.match_kind) ?? 'similar';
}

/** A short label for a document in a citation chip: the name without its extension. */
export function documentLabel(fileName: string): string {
  const base = fileName.replace(/\.[A-Za-z0-9]{1,5}$/, '');
  return base.length > 36 ? `${base.slice(0, 35)}…` : base;
}

/** The sentence for a confirm that one candidate stopped. Nothing was added. */
export function refusalMessage(refusal: CandidateRefusal): string {
  const which = refusal.ordinal != null ? `Candidate ${refusal.ordinal}` : 'One candidate';
  const why =
    refusal.cause === 'exact_duplicate'
      ? refusal.existingNumber
        ? `its module already has a requirement with the same text, ${refusal.existingNumber}`
        : 'its module already has a requirement with the same text'
      : refusal.cause === 'module_retired'
        ? 'its module has been retired'
        : 'its module refused it';
  return `${which} could not be added: ${why}. Nothing was added. Skip it or move it to another module, then confirm again.`;
}

/** The toast after a confirm (prototype `confirmAi`). */
export function confirmedMessage(added: number, moduleCount: number): string {
  return `${plural(added, 'requirement')} added to drafts in ${plural(moduleCount, 'module')}. Review and publish each one.`;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** When a run happened, in the app's one date-and-time format (C2-45). */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  if (Number.isNaN(new Date(iso).getTime())) return '';
  return formatDateTime(iso);
}

/** Cents as dollars, for a run's cost. */
export function formatCost(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '';
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

/**
 * Split a page's text around the cited range `[start, end)`. Out-of-range
 * offsets are clamped, so a bad range can never throw or hide the page.
 */
export function splitAtRange(
  text: string,
  start: number,
  end: number,
): { before: string; cited: string; after: string } {
  const from = Math.max(0, Math.min(start, text.length));
  const to = Math.max(from, Math.min(end, text.length));
  return { before: text.slice(0, from), cited: text.slice(from, to), after: text.slice(to) };
}

// ---------------------------------------------------------------------------
// Where a cited passage is (T-0155)
// ---------------------------------------------------------------------------

/** Said wherever a citation into a file with no recorded pages is shown. */
export const NO_PAGES_NOTE =
  'This file does not record pages, so the passage is located by its section.';

/** The deepest heading of a trail: "3 Financials > 3.2 Accounts Payable" -> "3.2 Accounts Payable". */
export function lastSection(trail: string | null | undefined): string | null {
  if (!trail) return null;
  const parts = trail.split(' > ');
  return parts[parts.length - 1] || null;
}

/**
 * A citation's location. Servers before T-0155 sent none: then it is read
 * from the document's `page_source` (absent too: a page, as before).
 */
export function citationLocation(
  cite: Pick<IntakeCitation, 'page_number' | 'section_path' | 'location'>,
  pageSource?: string | null,
): IntakeLocation {
  if (cite.location) return cite.location;
  if (pageSource === 'sheets') {
    return {
      kind: 'sheet_row',
      sheet: cite.section_path ?? `Sheet ${cite.page_number}`,
      row: null,
    };
  }
  if (pageSource === 'none')
    return { kind: 'section', section: cite.section_path ?? null, paragraph: null };
  return { kind: 'page', page: cite.page_number, section: cite.section_path ?? null };
}

/**
 * A location in plain words: "p. 7 · 3.2 Accounts Payable",
 * "Requirements, row 14", "2 Payables, paragraph 3". Never a page number
 * for a file that records no pages.
 */
export function locationWords(location: IntakeLocation): string {
  switch (location.kind) {
    case 'page': {
      const section = lastSection(location.section);
      return section ? `p. ${location.page} · ${section}` : `p. ${location.page}`;
    }
    case 'section': {
      const section = lastSection(location.section);
      const paragraph = location.paragraph ? `paragraph ${location.paragraph}` : null;
      if (section && paragraph) return `${section}, ${paragraph}`;
      return section ?? (paragraph ? `No pages recorded, ${paragraph}` : 'No pages recorded');
    }
    case 'sheet_row':
      return location.row != null ? `${location.sheet}, row ${location.row}` : location.sheet;
  }
}

/** The location fields of the shared citation chip. */
export function locationChip(
  location: IntakeLocation,
): Pick<CitationChipData, 'page' | 'section' | 'sheet' | 'row' | 'noPages'> {
  switch (location.kind) {
    case 'page':
      return { page: location.page, section: lastSection(location.section) };
    case 'section':
      return { noPages: true, section: lastSection(location.section) };
    case 'sheet_row':
      return { sheet: location.sheet, row: location.row ?? null };
  }
}
